import { useEffect, useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { useLazyQuery } from '@apollo/client/react';
import { Alert, Button, Timeline } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';

import AgentNote from './AgentNote';

import Loading from 'components/Loading/Loading';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';

import { PAGE_VIEWS_QUERY } from 'queries/notes';

const INITIAL_PAGE = 0;
const PAGE_SIZE = 100;
const BODY_HEIGHT = '400px';

const ProfileViews = () => {
  const { customerId, applicationId } = useParams();
  const rootRef = useRef(null);
  const [profileViews, setProfileViews] = useState([]);
  const [nextPage, setNextPage] = useState(INITIAL_PAGE);
  const [showLoadMoreFailure, setShowLoadMoreFailure] = useState(false);

  const profileViewsTimeline = useMemo(() => {
    const views = profileViews || [];
    return views?.map((profileView, index) => {
      return {
        icon: <ClockCircleOutlined />,
        content: (
          <div key={`interaction-itm-${index}`} data-testid="interaction-item">
            <AgentNote interaction={profileView} rootRef={rootRef} />
          </div>
        ),
      };
    });
  }, [profileViews]);

  const [getProfileViews, profileViewsQuery] = useLazyQuery(PAGE_VIEWS_QUERY);
  const { error, loading, data } = profileViewsQuery;

  const fetchPage = (page = INITIAL_PAGE) => {
    if (page === -1) {
      return;
    }

    setShowLoadMoreFailure(false);
    getProfileViews({
      variables: {
        customerId,
        applicationId,
        page,
        size: PAGE_SIZE,
      },
    });
  };

  useEffect(() => {
    setProfileViews([]);
    setNextPage(INITIAL_PAGE);
    fetchPage(INITIAL_PAGE);
  }, [customerId, applicationId]);

  useEffect(() => {
    if (error) {
      if (nextPage > INITIAL_PAGE) {
        setShowLoadMoreFailure(true);
      }
      return;
    }

    const views = data?.views;

    if (!loading && views) {
      const profileViewsWithMessageAndTz = views.map((view) => ({
        ...view,
        timestamp: view.toMcInteractionDto.timestamp,
        message: view.toMcInteractionDto.message,
      }));

      setProfileViews([...profileViews, ...profileViewsWithMessageAndTz]);

      const isOnLastPage = views?.length < 100;
      setNextPage((prev) => (isOnLastPage ? -1 : prev + 1));
    }
  }, [error, loading, data]);

  const showSkeleton = loading && nextPage === 0;
  const loadMoreButtonText = nextPage !== -1 ? 'Load More' : 'No More Views';

  return (
    <div style={{ overflow: 'auto', paddingTop: 'var(--spacing-sm)' }}>
      <QueryBoundary
        messageHeight={BODY_HEIGHT}
        errorMessage="Failed to load profile views."
        query={{
          loading: showSkeleton,
          called: true,
          data: {},
        }}
        skeleton={{
          title: false,
          paragraph: { rows: 12 },
        }}
      >
        <Timeline
          mode="start"
          items={profileViewsTimeline}
          style={{ paddingLeft: '2px', paddingTop: '2px' }}
        />

        {showLoadMoreFailure ? (
          <Alert
            type="error"
            title="Failed to load more profile views. Please try again."
            style={{ marginBottom: 'var(--spacing)' }}
          />
        ) : null}

        {loading ? (
          <Loading />
        ) : (
          <>
            <Button
              type="primary"
              onClick={() => fetchPage(nextPage)}
              disabled={nextPage === -1 || loading}
            >
              {loadMoreButtonText}
            </Button>
          </>
        )}
      </QueryBoundary>
    </div>
  );
};

export default ProfileViews;
